import { IdSchema, TimestampSchema } from "@uzverse/core";
import { z } from "zod";
/**
 * Contracts of the world asset pipeline (docs/PHASE2_PLAN.md 2.9 and package 7): what the owner
 * review queue, the credits page and the client catalog read. The blob itself is served by
 * `WORLD_ROUTES.asset` (`/api/v1/assets/:sha256`), approved assets only.
 */
export const ASSET_ROUTES = {
    /** GET → `AssetCatalog`: approved assets (id, sha256, bounds, format) for the client loader. */
    catalog: "/api/v1/world/assets",
    /** GET → `AssetCredits`: approved assets with author and license attribution. */
    credits: "/api/v1/world/credits",
};
/** Owner-panel routes (owner-api, session auth, World Admin = the Owner). */
export const OWNER_ASSET_ROUTES = {
    /** GET `?status=` → `{ assets: AssetAdminItem[] }`. */
    list: "/owner/world/assets",
    /** POST `{ decision, note? }` → `{ status, version }`. */
    review: "/owner/world/assets/:assetId/review",
    /** GET → the optional preview image of a pending asset (PNG). */
    thumbnail: "/owner/world/assets/:sha256/thumbnail",
    /** GET → `{ approvals: AssetFetchApproval[] }`: agent download requests awaiting approval. */
    fetches: "/owner/world/asset-fetches",
    /** POST `{ decision, note? }`: allow or refuse one download. */
    fetchDecision: "/owner/world/asset-fetches/:approvalId/decision",
};
/** Routes of `services/world` behind the owner routes (service token, audience `world`). */
export const WORLD_ASSET_ADMIN_API = {
    list: "/v1/asset-admin/assets",
    thumbnail: "/v1/asset-admin/assets/:sha256/thumbnail",
    fetches: "/v1/asset-admin/fetches",
    fetchDecision: "/v1/asset-admin/fetches/:approvalId/decision",
    catalog: "/v1/asset-catalog",
    credits: "/v1/credits",
};
export const AssetStatusSchema = z.enum(["pending_review", "approved", "rejected"]);
export const AutoReviewSchema = z.object({
    /** `pass`: nothing doubtful; `flags`: the reviewer listed doubts; `needs_human`: no review ran. */
    verdict: z.enum(["pass", "flags", "needs_human"]),
    flags: z.array(z.string().max(300)).max(20),
    summary: z.string().max(1000),
    /** Which tier ran (`strong` = the Opus tier through the model policy); absent without a run. */
    tier: z.string().max(20).optional(),
    model: z.string().max(100).optional(),
    at: TimestampSchema,
});
export const AssetAdminItemSchema = z.object({
    id: IdSchema,
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    name: z.string(),
    format: z.enum(["glb", "png", "jpg", "ktx2"]),
    mediaType: z.string(),
    sizeBytes: z.number().int(),
    triangles: z.number().int().optional(),
    tags: z.array(z.string()),
    status: AssetStatusSchema,
    license: z.object({
        spdx: z.string(),
        commercialUse: z.boolean(),
        attributionRequired: z.boolean(),
        author: z.string().optional(),
        attribution: z.string().optional(),
        sourceUrl: z.string(),
        retrievedAt: z.string(),
    }),
    source: z.object({ provider: z.string(), url: z.string() }),
    autoReview: AutoReviewSchema.optional(),
    reviewNote: z.string().optional(),
    hasThumbnail: z.boolean(),
    /** Is the SPDX id on the world's allowlist right now (approval would be refused otherwise)? */
    licenseAllowed: z.boolean(),
});
export const AssetAdminListSchema = z.object({ assets: z.array(AssetAdminItemSchema) });
/** A download an agent asked for (`world.proposeAsset`), waiting for the owner's decision. */
export const AssetFetchApprovalSchema = z.object({
    id: IdSchema,
    requestedAt: TimestampSchema,
    /** Where the world would download from. */
    url: z.string(),
    name: z.string(),
    license: z.string(),
    author: z.string().optional(),
    requestedBy: z.string(),
});
export const AssetCatalogSchema = z.object({
    assets: z.array(z.object({
        id: IdSchema,
        sha256: z.string().regex(/^[0-9a-f]{64}$/),
        name: z.string(),
        format: z.enum(["glb", "png", "jpg", "ktx2"]),
        sizeBytes: z.number().int(),
        triangles: z.number().int().optional(),
        bounds: z
            .object({
            min: z.tuple([z.number(), z.number(), z.number()]),
            max: z.tuple([z.number(), z.number(), z.number()]),
        })
            .optional(),
    })),
});
export const AssetCreditsSchema = z.object({
    credits: z.array(z.object({
        sha256: z.string().regex(/^[0-9a-f]{64}$/),
        name: z.string(),
        author: z.string().nullable(),
        license: z.string(),
        attribution: z.string().nullable(),
        attributionRequired: z.boolean(),
        sourceUrl: z.string(),
    })),
});
/** World setting (scope world, default true): real-brand models may be shown. Turn off before the world opens to the public. */
export const ASSET_SETTING_ALLOW_BRANDED = "world.assets.allowBranded";
/** Whether the client may use this asset under the current world settings. */
export function isAssetAllowed(asset, settings = {}) {
    if (asset.branded === true && settings.allowBranded === false)
        return false;
    return true;
}
/** Keep only the assets `isAssetAllowed` accepts. */
export function filterAllowedAssets(assets, settings = {}) {
    return assets.filter((a) => isAssetAllowed(a, settings));
}
//# sourceMappingURL=assets.js.map